import { useEffect, useMemo, useState } from "react";
import { formatUzPhone, isBlankPhoneInput, phoneSearchQuery } from "../phone";
import {
  api,
  fmtDate,
  TARIFF_LABEL,
  TARIFF_ORDER,
  type Health,
  type Tariff,
  type WorkshopRow,
  type WorkshopStatus,
} from "../api";
import { HealthBadge, StatusBadge, TariffBadge } from "../components/Badge";
import { Modal } from "../components/Modal";
import { downloadCsv } from "../util/csv";
import type { Location } from "../routing";

type SortKey = "name" | "tariff" | "created_at" | "trial_end" | "clients_count" | "works_count" | "days_since_last_work";

// Enter/Space activates a non-button element (accessible headers & rows).
const onActivate = (fn: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); }
};

export function Workshops({
  onNavigate,
  showAdd,
  setShowAdd,
}: {
  onNavigate: (loc: Location) => void;
  showAdd: boolean;
  setShowAdd: (v: boolean) => void;
}) {
  const [rows, setRows] = useState<WorkshopRow[] | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [tab, setTab] = useState<"faol" | "arxiv">("faol");
  const [q, setQ] = useState("");
  const [filterTariff, setFilterTariff] = useState<"" | Tariff>("");
  const [filterStatus, setFilterStatus] = useState<"" | WorkshopStatus | "expired_or_trial_done">("");
  const [filterHealth, setFilterHealth] = useState<"" | Health>("");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [err, setErr] = useState<string | null>(null);
  const [reactivatingId, setReactivatingId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setRows(null);
        const w = await api.workshops(tab === "arxiv");
        setRows(w);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, [refresh, tab]);

  async function reactivate(id: number) {
    setReactivatingId(id);
    setErr(null);
    try {
      await api.updateWorkshop(id, { status: "active" });
      setRefresh((x) => x + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setReactivatingId(null);
    }
  }

  const filtered = useMemo(() => {
    if (!rows) return [];
    const ql = q.trim().toLowerCase();
    const phoneNeedle = phoneSearchQuery(q).toLowerCase();
    let out = rows;
    if (ql) out = out.filter((r) =>
      r.name.toLowerCase().includes(ql) ||
      (r.owner_name ?? "").toLowerCase().includes(ql) ||
      (r.owner_phone ?? "").toLowerCase().includes(phoneNeedle)
    );
    // Tarif/holat filtrlari faqat "Faol" tabda — arxiv ro'yxatini ular
    // (masalan filterStatus="active") yashirib qo'ymasligi uchun.
    if (tab === "faol") {
      if (filterTariff) out = out.filter((r) => r.tariff === filterTariff);
      if (filterHealth) out = out.filter((r) => r.health === filterHealth);
      if (filterStatus === "expired_or_trial_done") {
        out = out.filter((r) => r.is_expired || r.status === "expired");
      } else if (filterStatus) {
        out = out.filter((r) => r.status === filterStatus);
      }
    }
    const sorted = [...out].sort((a, b) => {
      const av = a[sortKey] as string | number | null;
      const bv = b[sortKey] as string | number | null;
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp = av < bv ? -1 : 1;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [rows, q, filterTariff, filterStatus, filterHealth, sortKey, sortDir, tab]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  }
  function sortIcon(k: SortKey) {
    if (sortKey !== k) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  function exportCsv() {
    const header = ["ID", "Nomi", "Egasi", "Telefon", "Tarif", "Holat", "Trial tugashi", "Mijozlar", "Ishlar", "Yaratilgan"];
    const data = filtered.map((r) => [
      r.id, r.name, r.owner_name ?? "", r.owner_phone ?? "",
      TARIFF_LABEL[r.tariff], r.status,
      r.trial_end ? new Date(r.trial_end).toISOString().slice(0, 10) : "",
      r.clients_count, r.works_count,
      r.created_at ? new Date(r.created_at).toISOString().slice(0, 10) : "",
    ]);
    downloadCsv(`ustaxonalar-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...data]);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Ustaxonalar</h1>
          <p className="page-sub">Platformadagi barcha ustaxonalarni boshqarish</p>
        </div>
        <div className="actions">
          <div className="chips">
            <button className={tab === "faol" ? "active" : ""} onClick={() => setTab("faol")}>Faol</button>
            <button className={tab === "arxiv" ? "active" : ""} onClick={() => setTab("arxiv")}>Arxivlangan</button>
          </div>
          <button className="btn ghost" onClick={exportCsv} disabled={!rows || rows.length === 0}>CSV eksport</button>
          <button className="btn" onClick={() => setShowAdd(true)}>+ Yangi ustaxona</button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {tab === "faol" && (
        <div className="chips" style={{ marginBottom: 12 }}>
          <button className={filterHealth === "" ? "active" : ""} onClick={() => setFilterHealth("")}>Hammasi</button>
          <button className={filterHealth === "red" ? "active" : ""} onClick={() => setFilterHealth("red")}>🔴 Xavf</button>
          <button className={filterHealth === "yellow" ? "active" : ""} onClick={() => setFilterHealth("yellow")}>🟡 Sust</button>
          <button className={filterHealth === "green" ? "active" : ""} onClick={() => setFilterHealth("green")}>🟢 Sog'lom</button>
        </div>
      )}

      {tab === "faol" && (
      <div className="filters">
        <label className="grow">
          Qidiruv
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, egasi yoki telefon" />
        </label>
        <label>
          Tarif
          <select value={filterTariff} onChange={(e) => setFilterTariff(e.target.value as "" | Tariff)}>
            <option value="">Hammasi</option>
            {TARIFF_ORDER.map((t) => (
              <option key={t} value={t}>{TARIFF_LABEL[t]}</option>
            ))}
          </select>
        </label>
        <label>
          Holat
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as "" | WorkshopStatus | "expired_or_trial_done")}>
            <option value="">Hammasi</option>
            <option value="active">Faol</option>
            <option value="suspended">To'xtatilgan</option>
            <option value="expired_or_trial_done">Muddati tugagan</option>
          </select>
        </label>
      </div>
      )}

      <div className="panel flush">
        {!rows ? (
          <div className="empty">Yuklanmoqda…</div>
        ) : filtered.length === 0 ? (
          <div className="empty">{tab === "arxiv" ? "Arxivlangan ustaxona yo'q" : "Mos ustaxona topilmadi"}</div>
        ) : tab === "arxiv" ? (
          <table className="table">
            <thead><tr>
              <th>ID</th>
              <th>Nomi</th>
              <th>Egasi telefoni</th>
              <th>Arxivlangan sana</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="clickable" role="link" tabIndex={0} onClick={() => onNavigate({ route: "workshop-detail", id: r.id })} onKeyDown={onActivate(() => onNavigate({ route: "workshop-detail", id: r.id }))}>
                  <td className="muted">#{r.id}</td>
                  <td><strong>{r.name}</strong></td>
                  <td>{formatUzPhone(r.owner_phone) || "—"}</td>
                  <td>{fmtDate(r.archived_at)}</td>
                  <td className="right">
                    <button
                      className="btn"
                      disabled={reactivatingId === r.id}
                      onClick={(e) => { e.stopPropagation(); reactivate(r.id); }}
                    >{reactivatingId === r.id ? "..." : "Faollashtirish"}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="table">
            <thead><tr>
              <th className="sortable" role="button" tabIndex={0} onClick={() => toggleSort("name")} onKeyDown={onActivate(() => toggleSort("name"))}>Nomi{sortIcon("name")}</th>
              <th>Salomatlik</th>
              <th>Egasi</th>
              <th className="sortable" role="button" tabIndex={0} onClick={() => toggleSort("tariff")} onKeyDown={onActivate(() => toggleSort("tariff"))}>Tarif{sortIcon("tariff")}</th>
              <th>Holat</th>
              <th className="num sortable" role="button" tabIndex={0} onClick={() => toggleSort("days_since_last_work")} onKeyDown={onActivate(() => toggleSort("days_since_last_work"))}>Oxirgi ish{sortIcon("days_since_last_work")}</th>
              <th className="num sortable" role="button" tabIndex={0} onClick={() => toggleSort("clients_count")} onKeyDown={onActivate(() => toggleSort("clients_count"))}>Mijoz{sortIcon("clients_count")}</th>
              <th className="num sortable" role="button" tabIndex={0} onClick={() => toggleSort("works_count")} onKeyDown={onActivate(() => toggleSort("works_count"))}>Ish{sortIcon("works_count")}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="clickable" role="link" tabIndex={0} onClick={() => onNavigate({ route: "workshop-detail", id: r.id })} onKeyDown={onActivate(() => onNavigate({ route: "workshop-detail", id: r.id }))}>
                  <td><strong>{r.name}</strong></td>
                  <td><HealthBadge health={r.health} reason={r.reason} /></td>
                  <td>{r.owner_name || "—"}<div className="muted" style={{ fontSize: 12 }}>{formatUzPhone(r.owner_phone)}</div></td>
                  <td><TariffBadge tariff={r.tariff} /></td>
                  <td><StatusBadge ws={r} /></td>
                  <td className="num">{r.days_since_last_work === null ? "—" : `${r.days_since_last_work} kun`}</td>
                  <td className="num">{r.clients_count}</td>
                  <td className="num">{r.works_count}</td>
                  <td className="right"><button className="icon-btn" onClick={(e) => { e.stopPropagation(); onNavigate({ route: "workshop-detail", id: r.id }); }}>Ochish →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <CreateModal
          onClose={() => setShowAdd(false)}
          onSaved={(id) => {
            setShowAdd(false);
            setRefresh((x) => x + 1);
            onNavigate({ route: "workshop-detail", id });
          }}
        />
      )}
    </>
  );
}

function CreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id: number) => void }) {
  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("+998");
  const [tariff, setTariff] = useState<Tariff>("trial");
  const [trialDays, setTrialDays] = useState("14");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isBlankPhoneInput(ownerPhone)) { setErr("Egasi telefonini kiriting"); return; }
    setErr(null); setBusy(true);
    try {
      const r = await api.createWorkshop({
        name,
        owner_full_name: ownerName,
        owner_phone: ownerPhone,
        tariff,
        trial_days: parseInt(trialDays, 10) || 0,
      });
      onSaved(r.workshop_id);
    } catch (e) {
      setErr((e as Error).message);
    } finally { setBusy(false); }
  }

  return (
    <Modal onClose={onClose} labelledBy="cw-title">
      <form onSubmit={submit}>
        <div className="modal-head">
          <h2 id="cw-title">Yangi ustaxona</h2>
          <div className="sub">Ustaxona, ega va asosiy filial bir vaqtda yaratiladi</div>
        </div>
        <div className="modal-body">
          {err && <div className="error">{err}</div>}
          <div className="form">
            <div className="field">
              <label>Ustaxona nomi</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Egasi ismi</label>
                <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} required />
              </div>
              <div className="field">
                <label>Egasi telefoni</label>
                <input value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} required placeholder="+998901234567" />
              </div>
            </div>
            <div className="field">
              <label>Boshlang'ich parol</label>
              <div className="note"><strong>12345678</strong></div>
              <div className="help">Egasi tizimga kirgach parolini o'zgartirishi mumkin.</div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Tarif</label>
                <select value={tariff} onChange={(e) => setTariff(e.target.value as Tariff)}>
                  {TARIFF_ORDER.map((t) => (
                    <option key={t} value={t}>{TARIFF_LABEL[t]}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Sinov muddati (kun)</label>
                <input value={trialDays} onChange={(e) => setTrialDays(e.target.value)} type="number" min="0" max="365" />
              </div>
            </div>
          </div>
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>Bekor</button>
          <button type="submit" className="btn" disabled={busy}>{busy ? "..." : "Yaratish"}</button>
        </div>
      </form>
    </Modal>
  );
}
